import { useState } from 'react';
import { toast } from 'react-toastify';
import NslMailAPI from '../../NslMailAPI';
import { domainsMatch } from './popupUtils';

export const useAliasOperations = (accessToken) => {
    const [existingAliases, setExistingAliases] = useState([]);
    const [allAliases, setAllAliases] = useState([]);

    const lookupExistingAliases = async (url, searchTerm = '') => {
        if (!url || !accessToken) return;
        
        try {
            const api = new NslMailAPI(accessToken);

            // If there's a search term, use server-side search
            if (searchTerm) {
                const aliases = await api.getUserAliases('custom', searchTerm, 'site_name', 'asc');
                setExistingAliases(aliases);
            } else {
                // For initial load, get all aliases and filter by domain
                const aliases = await api.getUserAliases('all', '', 'site_name', 'asc');
                setAllAliases(aliases);

                // Filter aliases that match the domain using smart matching
                // This will match:
                // - Exact domain matches
                // - Subdomain matches (app.sitename.com matches sitename.com)
                // - Base domain matches
                const matchingAliases = aliases.filter(alias =>
                    alias.site_url && domainsMatch(alias.site_url, url)
                );

                setExistingAliases(matchingAliases);
            }
        } catch (error) {
            console.error('Error looking up aliases:', error);
            toast.error('Error looking up existing aliases: ' + error.message);
        }
    };

    const createAlias = async (aliasData) => {
        if (!aliasData.site_name || !aliasData.site_url) {
            toast.error('Please fill in both site name and URL');
            return false;
        }
        
        if (!accessToken) {
            toast.error('You must be logged in to create aliases');
            return false;
        }
        
        try {
            const api = new NslMailAPI(accessToken);
            const payload = {
                site_name: aliasData.site_name,
                custom_url: aliasData.site_url,
                private_notes: aliasData.private_notes,
                alerts_enabled: aliasData.alerts_enabled,
                hidden: aliasData.hidden,
                blacklist_action: aliasData.blacklist_action
            };
            
            await api.api_post('/aliases', payload);
            toast.success('Alias created successfully!');
            return true;
        } catch (error) {
            console.error('Error creating alias:', error);
            toast.error('Error creating alias: ' + error.message);
            return false;
        }
    };

    const saveAlias = async (aliasData) => {
        if (!aliasData.site_name || !aliasData.site_url) {
            toast.error('Please fill in both site name and URL');
            return false;
        }
        
        if (!accessToken || !aliasData.id) {
            toast.error('You must be logged in to save aliases');
            return false;
        }
        
        try {
            const api = new NslMailAPI(accessToken);
            const payload = {
                id: aliasData.id,
                site_name: aliasData.site_name,
                custom_url: aliasData.site_url,
                private_notes: aliasData.private_notes,
                alerts_enabled: aliasData.alerts_enabled,
                hidden: aliasData.hidden,
                blacklist_action: aliasData.blacklist_action
            };
            
            await api.saveAlias(payload);
            toast.success('Alias saved successfully!');
            return true;
        } catch (error) {
            console.error('Error saving alias:', error);
            toast.error('Error saving alias: ' + error.message);
            return false;
        }
    };

    const getAliasDetails = async (aliasId) => {
        try {
            const api = new NslMailAPI(accessToken);
            const alias = await api.getAlias(aliasId);
            return alias;
        } catch (error) {
            console.error('Error loading alias:', error);
            toast.error('Error loading alias: ' + error.message);
            return null;
        }
    };

    const updateExistingAliases = (aliases) => {
        setExistingAliases(aliases);
    };

    return {
        existingAliases,
        allAliases,
        lookupExistingAliases,
        createAlias,
        saveAlias,
        getAliasDetails,
        updateExistingAliases
    };
};